import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@mui/material";
import React, { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { restrict } from "src/components/restrict";
import FormWrapper from "src/components/FormWrapper";
import { FormProvider, RHFTextField } from "src/components/hook-form";
import RHFAutocomplete from "src/components/hook-form/RHFAutocomplete";
import { masterGetData, masterSaveData } from "src/pages/api";
import * as Yup from "yup";

const defaultValues = {
  fullName: "",
  mobile: "",
  username: "",
  password: "",
  schoolID: null,
};

const schema = Yup.object().shape({
  fullName: Yup.string().trim().required("Required"),
  mobile: Yup.string()
    .trim()
    .required("Required")
    .matches(/^[6789]\d{9}$/, "Invalid Mobile Number"),
  username: Yup.string().trim().required("Required"),
  password: Yup.string().trim().required("Required"),
  schoolID: Yup.object().nullable().required("School is required"),
});

export default function AddThirdParty({ onClose, row, isEditMode }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const { handleSubmit, reset, setValue, watch } = methods;

  const { data: schools } = useQuery({
    queryKey: ["getData", "schools", 0, 100],
    queryFn: masterGetData,
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const mutation = useMutation((req) => masterSaveData({ req, isEditMode }), {
    onSuccess: () => {
      enqueueSnackbar(`Third Party ${isEditMode ? "Updated" : "Added"}`, {
        variant: "success",
      });
      queryClient.invalidateQueries(["getData", "third_party"]);
      onClose();
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || "Failed to Add", { variant: "error" });
    },
  });

  const onSubmit = (data) => {
    const req = {
      tableName: "third_party",
      fieldsArr: [
        {
          ...data,
          schoolID: data.schoolID?.id || null,
        },
      ],
    };
    mutation.mutate(req);
  };

  useEffect(() => {
    if (isEditMode && row) {
      reset({
        id: row?.id,
        fullName: row.fullName,
        mobile: row.mobile,
        username: row.username,
        password: row.password,
        schoolID: schools?.data?.find((school) => school.id === row.schoolID) || null,
      });
    }
  }, [isEditMode, row, schools, reset]);

  return (
    <FormWrapper
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      onReset={() => reset()}
      loading={mutation.isLoading}
      maxWidth="md"
      fullWidth
      isEditMode={isEditMode}
      title={isEditMode ? "Update Third Party" : "Add Third Party"}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <RHFAutocomplete
              name="schoolID"
              label="Select School"
              size="large"
              options={schools?.data || []}
              getOptionLabel={(option) => option.schoolName || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={6}>
            <RHFTextField name="fullName" label="Name" fullWidth required />
          </Grid>
          <Grid item xs={6}>
            <RHFTextField name="mobile" label="Mobile No." fullWidth required   inputProps={{ maxLength: 10 }}
              onInput={restrict.digits}/>
          </Grid>
          <Grid item xs={6}>
            <RHFTextField name="username" label="Username" fullWidth required />
          </Grid>
          <Grid item xs={6}>
            <RHFTextField name="password" label="Password" fullWidth required />
          </Grid>
        </Grid>
      </FormProvider>
    </FormWrapper>
  );
}
